import type { InfoField, RankingSchool } from './types';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat('en-US');

export function formatCurrency(value: number | undefined): string {
  return value != null ? currencyFormatter.format(value) : 'N/A';
}

export function formatPercent(value: number | undefined): string {
  return value != null ? percentFormatter.format(value) : 'N/A';
}

export function formatNumber(value: number | undefined): string {
  return value != null ? numberFormatter.format(value) : 'N/A';
}

export function padTuitionValues(
  inStateCurrencyStr: string,
  outStateCurrencyStr: string,
): [string, string] {
  const inLen = inStateCurrencyStr.length;
  const outLen = outStateCurrencyStr.length;

  if (inLen === outLen) return [inStateCurrencyStr, outStateCurrencyStr];

  if (inLen < outLen) {
    const padded = inStateCurrencyStr.replace(
      '$',
      `$${' '.repeat(outLen - inLen)}`,
    );
    return [padded, outStateCurrencyStr];
  }

  const padded = outStateCurrencyStr.replace(
    '$',
    `$${' '.repeat(inLen - outLen)}`,
  );
  return [inStateCurrencyStr, padded];
}

/**
 * Maps the Rango `schoolLevel` enumeration to the badge label.
 * The upstream values are exact strings, so we match them directly rather than via substring checks.
 * "At least 2 but less than 4 years" contains both "2" and "4", so fuzzy matching can misclassify 2-year schools as 4-year (EDUN-13132).
 * Returns an empty string for missing or unrecognized values so the duration badge is omitted.
 */
export function getDuration(schoolLevel?: string): string {
  if (!schoolLevel) return '';
  switch (schoolLevel.trim()) {
    case 'Four or more years':
      return '4 year';
    case 'At least 2 but less than 4 years':
      return '2 year';
    case 'Less Than 2 Years':
      return '<2 year';
    default:
      return '';
  }
}

/**
 * Buckets a school as on-campus, online, or both from the raw 0-100
 * `percentOnlinePrograms` value. Mirrors the legacy supersonic-ui
 * `schoolHelpers.getSchoolType`: only a school with zero online programs is
 * "Campus" and only a fully online school is "Online" — everything in between
 * is "Campus + Online" (EDUN-13132). A missing value yields no badge.
 */
export function getSchoolType(percentOnlinePrograms?: number): string {
  if (percentOnlinePrograms == null) return '';
  if (percentOnlinePrograms <= 0) return 'Campus';
  if (percentOnlinePrograms >= 100) return 'Online';
  return 'Campus + Online';
}

export function buildGeneralInfo(school: RankingSchool): string[] {
  const items: string[] = [];
  if (school.city && school.stateCode)
    items.push(`${school.city}, ${school.stateCode}`);
  const duration = getDuration(school.schoolLevel);
  if (duration) items.push(duration);
  const schoolType = getSchoolType(school.percentOnlinePrograms);
  if (schoolType) items.push(schoolType);
  return items;
}

export function buildDataPanel(
  school: RankingSchool,
  degreeLevel: 'undergrad' | 'grad',
): { stats: InfoField[]; details: InfoField[] } {
  const isGrad = degreeLevel === 'grad';

  const inStateLabel = isGrad
    ? 'Avg. Grad Tuition, In-State'
    : 'Avg. Undergrad Tuition, In-State';
  const outOfStateLabel = isGrad
    ? 'Avg. Grad Tuition, Out-of-State'
    : 'Avg. Undergrad Tuition, Out-of-State';
  const rawInState = isGrad
    ? school.gradTuitionInState
    : school.undergradTuitionInState;
  const rawOutOfState = isGrad
    ? school.gradTuitionOutOfState
    : school.undergradTuitionOutOfState;

  let inStateValue = formatCurrency(rawInState);
  let outOfStateValue = formatCurrency(rawOutOfState);

  if (inStateValue !== 'N/A' && outOfStateValue !== 'N/A') {
    [inStateValue, outOfStateValue] = padTuitionValues(
      inStateValue,
      outOfStateValue,
    );
  }

  const stats: InfoField[] = [
    {
      label: 'Online Enrollment',
      value: formatPercent(school.percentOnlineEnrollment),
    },
    {
      label: 'Accreditation',
      value: school.accreditation ? 'Yes' : 'No',
      tooltip: school.accreditation || undefined,
    },
    {
      label: 'Acceptance Rate',
      value: formatPercent(school.admissionRate),
    },
    {
      label: 'Graduation Rate',
      value: formatPercent(school.graduationRate),
    },
  ];

  const details: InfoField[] = [
    { label: inStateLabel, value: inStateValue },
    { label: outOfStateLabel, value: outOfStateValue },
    { label: 'Retention Rate', value: formatPercent(school.retentionRate) },
    {
      label: 'Students Enrolled',
      value: formatNumber(school.totalStudentsEnrolled),
    },
    {
      label: 'Institution Type',
      value:
        school.isPrivate != null
          ? school.isPrivate
            ? 'Private'
            : 'Public'
          : 'N/A',
    },
    {
      label: 'Accreditation',
      value: school.accreditation || 'N/A',
    },
  ];

  return { stats, details };
}

export function buildTestimonialsPanel(
  school: RankingSchool,
): InfoField[] | null {
  if (
    school.overallScore == null ||
    school.academicQuality == null ||
    school.recommend == null ||
    school.wouldAttendAgain == null
  ) {
    return null;
  }

  return [
    {
      label: 'Overall Score',
      value: formatPercent(school.overallScore),
      tooltip:
        'Students\u2019 general impression of their college experience as a whole.',
    },
    {
      label: 'Academic Quality',
      value: formatPercent(school.academicQuality),
      tooltip: 'How students rate the strength of academics.',
    },
    {
      label: 'Recommend',
      value: formatPercent(school.recommend),
      tooltip: 'How likely students are to recommend the school to others.',
    },
    {
      label: 'Would Attend Again',
      value: formatPercent(school.wouldAttendAgain),
      tooltip: 'How likely students would be to choose the same school again.',
    },
  ];
}
